import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNotificaciones } from '../../contextos/notificationContext';
import { EnumTipoNotificacion } from '../../interfaces';
import type { INotificacion } from './interfaces';
import { ORIGENES_NOTIFICACIONES } from './origenesNotificaciones';
import { CampanaNotificacionesVista } from './campanaNotificacionesVista';

/** No hay push al web: el contador se vuelve a consultar cada 5 minutos. */
const INTERVALO_CONSULTA_MS = 5 * 60 * 1000;

interface CampanaNotificacionesCVProps {
    /** Ids de los roles del usuario: definen qué orígenes le aplican. */
    codigosRol: number[];
}

/**
 * Campana de notificaciones de la barra superior: junta los orígenes que le aplican al usuario. Al abrirla, las
 * notificaciones se dan por vistas (se marcan leídas); en la lista abierta siguen resaltadas las nuevas.
 * Sin orígenes que le apliquen, no se muestra.
 */
export const CampanaNotificacionesCV = ({ codigosRol }: CampanaNotificacionesCVProps) => {
    const { mostrarNotificacion } = useNotificaciones();
    const claveRoles = codigosRol.join(',');
    const origenes = useMemo(
        () => Object.values(ORIGENES_NOTIFICACIONES).filter(o => o.aplica(codigosRol)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [claveRoles]
    );
    const [noLeidas, setNoLeidas] = useState(0);
    const [notificaciones, setNotificaciones] = useState<INotificacion[]>([]);
    const [abierta, setAbierta] = useState(false);
    const [cargando, setCargando] = useState(false);

    // El contador falla en silencio: se vuelve a intentar en la siguiente consulta, sin avisos cada 5 minutos.
    const consultarNoLeidas = useCallback(async () => {
        const conteos = await Promise.allSettled(origenes.map(o => o.contarNoLeidas()));
        setNoLeidas(conteos.reduce((total, c) => total + (c.status === 'fulfilled' ? c.value : 0), 0));
    }, [origenes]);

    useEffect(() => {
        if (origenes.length === 0) return;
        consultarNoLeidas();
        const intervalo = setInterval(consultarNoLeidas, INTERVALO_CONSULTA_MS);
        return () => clearInterval(intervalo);
    }, [origenes, consultarNoLeidas]);

    // Se marcan en el origen de cada una; las que fallen siguen contando para marcarlas la próxima vez.
    const marcarVistas = async (pendientes: INotificacion[]) => {
        if (pendientes.length === 0) return;
        const resultados = await Promise.allSettled(pendientes.map(n => ORIGENES_NOTIFICACIONES[n.Tipo].marcarLeida(n)));
        const fallidas = resultados.filter(r => r.status === 'rejected' || !r.value).length;
        setNoLeidas(fallidas);
        if (fallidas > 0) mostrarNotificacion(EnumTipoNotificacion.Warning, 'Notificaciones', 'Algunas notificaciones no se pudieron marcar como leídas.');
    };

    const consultarLista = async () => {
        setCargando(true);
        const listas = await Promise.allSettled(origenes.map(o => o.listar()));
        const todas = listas
            .flatMap(l => l.status === 'fulfilled' ? l.value : [])
            .sort((a, b) => b.Fecha.getTime() - a.Fecha.getTime());
        setNotificaciones(todas);
        setCargando(false);
        if (listas.some(l => l.status === 'rejected')) {
            mostrarNotificacion(EnumTipoNotificacion.Error, 'Notificaciones', 'No se pudieron consultar algunas notificaciones.');
        }
        // En segundo plano: la lista se muestra sin esperar las marcas.
        void marcarVistas(todas.filter(n => !n.Leida));
    };

    // Al cerrar, las que se acaban de ver dejan de resaltarse.
    const alternar = (abrir: boolean) => {
        setAbierta(abrir);
        if (abrir) consultarLista();
        else setNotificaciones(prev => prev.map(n => ({ ...n, Leida: true })));
    };

    if (origenes.length === 0) return null;

    return (
        <CampanaNotificacionesVista
            noLeidas={noLeidas}
            notificaciones={notificaciones}
            abierta={abierta}
            cargando={cargando}
            alAlternar={alternar}
        />
    );
};
